'use client';

import React, { useState, useMemo } from 'react';
import {
  ExtendedArchiMateDiagram,
  SecurityMetadata,
  SecurityZone,
  DataClassification,
  ComplianceFramework,
  ThreatLevel,
} from '@/lib/archimate/extended-types';
import {
  calculateSecurityScore,
  calculateDiagramSecurityScore,
  getElementsBySecurityZone,
  getVulnerableElements,
  getComplianceGaps,
  detectUnencryptedDataFlows,
  generateSecurityReport,
  getSecurityZoneColor,
  getThreatLevelColor,
  createDefaultSecurityMetadata,
  getComplianceFrameworkRequirements,
} from '@/lib/archimate/security-utils';
import { X, Shield, AlertTriangle, Download, Lock } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface SecurityPanelProps {
  diagram: ExtendedArchiMateDiagram;
  selectedElementId?: string;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onToggleHeatmap: (enabled: boolean) => void;
}

export default function SecurityPanel({
  diagram,
  selectedElementId,
  onUpdate,
  onClose,
  onToggleHeatmap,
}: SecurityPanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'element' | 'compliance'>('overview');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState<ComplianceFramework>('SOC2');

  const [elementSecurity, setElementSecurity] = useState<SecurityMetadata>(createDefaultSecurityMetadata);
  const [showHelp, setShowHelp] = useState(false);

  // Recalculate these whenever diagram or selectedFramework changes
  const overallScore = useMemo(() => calculateDiagramSecurityScore(diagram), [diagram]);
  const vulnerableElements = useMemo(() => getVulnerableElements(diagram), [diagram]);
  const unencryptedFlows = useMemo(() => detectUnencryptedDataFlows(diagram), [diagram]);
  const complianceGaps = useMemo(() => getComplianceGaps(diagram, selectedFramework), [diagram, selectedFramework]);

  const handleSave = () => {
    if (!selectedElementId) return;

    const updatedDiagram = {
      ...diagram,
      elementMetadata: {
        ...diagram.elementMetadata,
        [selectedElementId]: {
          ...diagram.elementMetadata[selectedElementId],
          security: elementSecurity,
        },
      },
    };

    onUpdate(updatedDiagram);
  };

  const handleToggleHeatmap = () => {
    const newState = !showHeatmap;
    setShowHeatmap(newState);
    onToggleHeatmap(newState);
  };

  const handleExportReport = () => {
    const report = generateSecurityReport(diagram);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-security-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load element security if selected
  React.useEffect(() => {
    if (selectedElementId) {
      const metadata = diagram.elementMetadata[selectedElementId];
      if (metadata?.security) {
        setElementSecurity(metadata.security);
      } else {
        setElementSecurity(createDefaultSecurityMetadata());
      }
      setViewMode('element');
    }
  }, [selectedElementId, diagram.elementMetadata]);

  const selectedElement = selectedElementId
    ? diagram.elements.find((e) => e.id === selectedElementId)
    : null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-accent';
    return 'text-red-500';
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[550px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Security & Compliance</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-cream/10 rounded transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="security"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'overview' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('element')}
            disabled={!selectedElementId}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'element' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Element
          </button>
          <button
            onClick={() => setViewMode('compliance')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'compliance' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Compliance
          </button>
          <button
            onClick={handleToggleHeatmap}
            className={`ml-auto px-4 py-2 rounded transition-colors flex items-center gap-2 ${
              showHeatmap ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            <Lock className="w-4 h-4" />
            Heatmap
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="p-6 bg-cream/5 border border-border rounded text-center">
              <div className="text-sm text-foreground-secondary mb-2">Overall Security Score</div>
              <div className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
                {overallScore.toFixed(0)}<span className="text-2xl">/100</span>
              </div>
            </div>

            {/* Security Zones */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-3">Security Zones</h3>
              <div className="space-y-2">
                {(['public', 'dmz', 'trusted', 'restricted', 'critical'] as SecurityZone[]).map((zone) => {
                  const elements = getElementsBySecurityZone(diagram, zone);
                  return (
                    <div key={zone} className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: getSecurityZoneColor(zone) }}
                      />
                      <div className="flex-1 capitalize">{zone}</div>
                      <div className="text-foreground-secondary">{elements.length} elements</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Vulnerabilities */}
            {vulnerableElements.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Vulnerabilities ({vulnerableElements.length})
                </h3>
                <div className="space-y-2">
                  {vulnerableElements.slice(0, 5).map(({ element, vulnerabilities }) => (
                    <div key={element.id} className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="font-medium text-foreground mb-1">{element.name}</div>
                      <div className="space-y-1">
                        {vulnerabilities.map((vuln) => (
                          <div key={vuln.id} className="text-sm text-red-400 flex items-center gap-2">
                            <span className="uppercase font-bold">{vuln.severity}</span>
                            <span>•</span>
                            <span>{vuln.description}</span>
                            {vuln.cve && <span className="text-xs">({vuln.cve})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unencrypted Flows */}
            {unencryptedFlows.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Unencrypted Data Flows ({unencryptedFlows.length})
                </h3>
                <div className="space-y-2">
                  {unencryptedFlows.slice(0, 5).map(({ source, target, reason }, index) => (
                    <div key={index} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="text-sm text-foreground mb-1">
                        {source.name} → {target.name}
                      </div>
                      <div className="text-xs text-yellow-400">{reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <button
              onClick={handleExportReport}
              className="w-full px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Security Report
            </button>
          </div>
        )}

        {/* Element Security Editor */}
        {viewMode === 'element' && selectedElement && (
          <div className="space-y-4">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="font-bold text-foreground">{selectedElement.name}</h3>
              <p className="text-sm text-foreground-tertiary">
                {selectedElement.type} • {selectedElement.layer}
              </p>
            </div>

            <div className="space-y-3">
              {/* Security Zone */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Security Zone
                  <span className="ml-2 text-xs text-foreground-tertiary">(impacts score: public -25, dmz -15, trusted -5, restricted +5)</span>
                </label>
                <select
                  value={elementSecurity.zone}
                  onChange={(e) =>
                    setElementSecurity({ ...elementSecurity, zone: e.target.value as SecurityZone })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="public">Public - Internet-facing, highest risk</option>
                  <option value="dmz">DMZ - Semi-exposed, high risk</option>
                  <option value="trusted">Trusted - Internal network, moderate risk</option>
                  <option value="restricted">Restricted - Additional controls, lower risk</option>
                  <option value="critical">Critical - Must have perfect security</option>
                </select>
                {elementSecurity.zone && (
                  <p className="text-xs text-foreground-tertiary mt-1">
                    {elementSecurity.zone === 'public' && 'External users can access. Requires strong controls.'}
                    {elementSecurity.zone === 'dmz' && 'Protected but accessible from internet. Firewall required.'}
                    {elementSecurity.zone === 'trusted' && 'Internal network only. Standard corporate controls.'}
                    {elementSecurity.zone === 'restricted' && 'Sensitive area with enhanced access controls.'}
                    {elementSecurity.zone === 'critical' && 'Mission-critical systems. Must maintain 90+ security score.'}
                  </p>
                )}
              </div>

              {/* Data Classification & Handling */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Data Classification & Handling</h4>

                <div>
                  <label className="block text-sm font-medium text-foreground-secondary mb-2">Data Classification</label>
                  <select
                    value={elementSecurity.dataClassification}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        dataClassification: e.target.value as DataClassification,
                      })
                    }
                    className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                  >
                    <option value="public">Public - No sensitivity</option>
                    <option value="internal">Internal - Company confidential (-5 score)</option>
                    <option value="confidential">Confidential - Highly sensitive (-12 score)</option>
                    <option value="pii">PII - Personal Identifiable Information (-18 score)</option>
                    <option value="pci">PCI - Payment Card Industry data (-20 score)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={elementSecurity.handlesPersonalData}
                      onChange={(e) =>
                        setElementSecurity({ ...elementSecurity, handlesPersonalData: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Handles Personal Data (GDPR/Privacy laws apply)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={elementSecurity.handlesFinancialData}
                      onChange={(e) =>
                        setElementSecurity({ ...elementSecurity, handlesFinancialData: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Handles Financial Data (PCI-DSS may apply)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={elementSecurity.handlesHealthData}
                      onChange={(e) =>
                        setElementSecurity({ ...elementSecurity, handlesHealthData: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Handles Health Data (HIPAA may apply)</span>
                  </label>
                </div>

                {(elementSecurity.handlesPersonalData || elementSecurity.handlesFinancialData || elementSecurity.handlesHealthData) && (
                  <div className="pt-2 border-t border-border-muted">
                    <label className="block text-sm font-medium text-foreground-secondary mb-2">Data Retention (days)</label>
                    <input
                      type="number"
                      value={elementSecurity.dataRetentionDays || ''}
                      onChange={(e) =>
                        setElementSecurity({ ...elementSecurity, dataRetentionDays: parseInt(e.target.value) || undefined })
                      }
                      placeholder="e.g., 90, 365, 2555 (7 years)"
                      className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                    />
                    <p className="text-xs text-foreground-tertiary mt-1">GDPR: typically 30-90 days. Financial: 7 years. Health: varies by region.</p>
                  </div>
                )}
              </div>

              {/* Encryption */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Encryption</h4>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={elementSecurity.encryption.atRest}
                      onChange={(e) =>
                        setElementSecurity({
                          ...elementSecurity,
                          encryption: { ...elementSecurity.encryption, atRest: e.target.checked },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Encrypted at Rest (+5 score)</span>
                  </label>

                  {elementSecurity.encryption.atRest && (
                    <div className="ml-6 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-foreground-secondary mb-1">Algorithm</label>
                        <input
                          type="text"
                          value={elementSecurity.encryption.atRestAlgorithm || ''}
                          onChange={(e) =>
                            setElementSecurity({
                              ...elementSecurity,
                              encryption: { ...elementSecurity.encryption, atRestAlgorithm: e.target.value },
                            })
                          }
                          placeholder="e.g., AES-256-GCM, ChaCha20-Poly1305"
                          className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                        />
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={elementSecurity.encryption.inTransit}
                      onChange={(e) =>
                        setElementSecurity({
                          ...elementSecurity,
                          encryption: { ...elementSecurity.encryption, inTransit: e.target.checked },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Encrypted in Transit (+5 score)</span>
                  </label>

                  {elementSecurity.encryption.inTransit && (
                    <div className="ml-6 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-foreground-secondary mb-1">Protocol</label>
                        <input
                          type="text"
                          value={elementSecurity.encryption.inTransitProtocol || ''}
                          onChange={(e) =>
                            setElementSecurity({
                              ...elementSecurity,
                              encryption: { ...elementSecurity.encryption, inTransitProtocol: e.target.value },
                            })
                          }
                          placeholder="e.g., TLS 1.3, TLS 1.2"
                          className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                        />
                      </div>
                    </div>
                  )}

                  {(elementSecurity.encryption.atRest || elementSecurity.encryption.inTransit) && (
                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">Key Management</label>
                      <input
                        type="text"
                        value={elementSecurity.encryption.keyManagement || ''}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            encryption: { ...elementSecurity.encryption, keyManagement: e.target.value },
                          })
                        }
                        placeholder="e.g., AWS KMS, Azure Key Vault, HashiCorp Vault"
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Authentication & Authorization */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Authentication & Authorization</h4>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.authentication.required}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        authentication: { ...elementSecurity.authentication, required: e.target.checked },
                        authenticationRequired: e.target.checked, // Backward compatibility
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Authentication Required</span>
                </label>

                {elementSecurity.authentication.required && (
                  <div className="ml-6 space-y-3 pt-2 border-t border-border-muted">
                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">
                        Authentication Type
                        <span className="ml-2 text-foreground-tertiary">(affects score: basic -12, mutual-TLS +2)</span>
                      </label>
                      <select
                        value={elementSecurity.authentication.type}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            authentication: {
                              ...elementSecurity.authentication,
                              type: e.target.value as any,
                            },
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      >
                        <option value="basic">Basic Auth (username/password)</option>
                        <option value="api-key">API Key</option>
                        <option value="jwt">JWT (JSON Web Token)</option>
                        <option value="oauth2">OAuth 2.0</option>
                        <option value="saml">SAML</option>
                        <option value="mutual-tls">Mutual TLS (strongest)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">
                        Authentication Context
                        <span className="ml-2 text-foreground-tertiary">(who authenticates?)</span>
                      </label>
                      <select
                        value={elementSecurity.authentication.context}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            authentication: {
                              ...elementSecurity.authentication,
                              context: e.target.value as any,
                            },
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      >
                        <option value="end-user">End Users (customers, public)</option>
                        <option value="admin">Administrators/Staff (internal)</option>
                        <option value="service-to-service">Service-to-Service (APIs)</option>
                        <option value="api">API Clients (external integrations)</option>
                        <option value="mixed">Mixed (multiple types)</option>
                      </select>
                      <p className="text-xs text-foreground-tertiary mt-1">
                        {elementSecurity.authentication.context === 'admin' && 'Admin access requires MFA (-15 score penalty without)'}
                        {elementSecurity.authentication.context === 'end-user' && 'End-user access benefits from MFA (-10 score penalty without)'}
                        {elementSecurity.authentication.context === 'service-to-service' && 'Consider mutual-TLS or API keys with rotation'}
                        {elementSecurity.authentication.context === 'api' && 'API authentication should include rate limiting and key rotation'}
                        {elementSecurity.authentication.context === 'mixed' && 'Mixed contexts require careful access control design'}
                      </p>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.authentication.mfaRequired}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            authentication: { ...elementSecurity.authentication, mfaRequired: e.target.checked },
                            mfa: e.target.checked, // Backward compatibility
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-foreground">
                        Multi-Factor Authentication (MFA) Required
                        {(elementSecurity.authentication.context === 'admin' || elementSecurity.authentication.context === 'end-user') &&
                          ' (+3 score)'}
                      </span>
                    </label>

                    {elementSecurity.authentication.mfaRequired && (
                      <div className="ml-6">
                        <label className="block text-xs font-medium text-foreground-secondary mb-1">MFA Method</label>
                        <input
                          type="text"
                          value={elementSecurity.authentication.mfaMethod || ''}
                          onChange={(e) =>
                            setElementSecurity({
                              ...elementSecurity,
                              authentication: { ...elementSecurity.authentication, mfaMethod: e.target.value },
                            })
                          }
                          placeholder="e.g., TOTP, WebAuthn, SMS, Hardware Token"
                          className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">Session Timeout (minutes)</label>
                      <input
                        type="number"
                        value={elementSecurity.authentication.sessionTimeout || ''}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            authentication: {
                              ...elementSecurity.authentication,
                              sessionTimeout: parseInt(e.target.value) || undefined,
                            },
                          })
                        }
                        placeholder="e.g., 30, 60, 480"
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t border-border-muted">
                  <label className="block text-xs font-medium text-foreground-secondary mb-2">Authorization Model</label>
                  <select
                    value={elementSecurity.authorization.model}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        authorization: { ...elementSecurity.authorization, model: e.target.value as any },
                      })
                    }
                    className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                  >
                    <option value="none">None</option>
                    <option value="rbac">RBAC (Role-Based Access Control)</option>
                    <option value="abac">ABAC (Attribute-Based Access Control)</option>
                    <option value="acl">ACL (Access Control Lists)</option>
                    <option value="dac">DAC (Discretionary Access Control)</option>
                    <option value="mac">MAC (Mandatory Access Control)</option>
                  </select>

                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={elementSecurity.authorization.leastPrivilege}
                      onChange={(e) =>
                        setElementSecurity({
                          ...elementSecurity,
                          authorization: { ...elementSecurity.authorization, leastPrivilege: e.target.checked },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Least Privilege Principle Applied (+2 score)</span>
                  </label>
                </div>
              </div>

              {/* Logging & Monitoring */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Logging & Monitoring</h4>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.logging?.enabled || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        logging: { ...elementSecurity.logging, enabled: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Logging Enabled (+3 score)</span>
                </label>

                {elementSecurity.logging?.enabled && (
                  <div className="ml-6 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">Log Level</label>
                      <select
                        value={elementSecurity.logging?.level || ''}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            logging: { ...elementSecurity.logging, level: e.target.value },
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      >
                        <option value="">Select level...</option>
                        <option value="DEBUG">DEBUG</option>
                        <option value="INFO">INFO</option>
                        <option value="WARN">WARN</option>
                        <option value="ERROR">ERROR</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">Retention (days)</label>
                      <input
                        type="number"
                        value={elementSecurity.logging?.retention || ''}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            logging: {
                              ...elementSecurity.logging,
                              retention: parseInt(e.target.value) || undefined,
                            },
                          })
                        }
                        placeholder="e.g., 90, 365"
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      />
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.logging?.piiRedaction || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            logging: { ...elementSecurity.logging, piiRedaction: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">PII Redaction (+2 score)</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.logging?.auditTrail || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            logging: { ...elementSecurity.logging, auditTrail: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">Audit Trail (+3 score)</span>
                    </label>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.monitoring?.enabled || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        monitoring: { ...elementSecurity.monitoring, enabled: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Monitoring Enabled (+2 score)</span>
                </label>

                {elementSecurity.monitoring?.enabled && (
                  <div className="ml-6 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.monitoring?.alerting || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            monitoring: { ...elementSecurity.monitoring, alerting: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">Alerting Configured (+2 score)</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.monitoring?.siem || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            monitoring: { ...elementSecurity.monitoring, siem: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">SIEM Integration (+5 score)</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Network Security */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Network Security</h4>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.internetFacing}
                    onChange={(e) =>
                      setElementSecurity({ ...elementSecurity, internetFacing: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Internet-Facing (-10 score)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.network?.firewalled || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        network: { ...elementSecurity.network, firewalled: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Firewall Protection (+5 score)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.network?.segmented || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        network: { ...elementSecurity.network, segmented: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Network Segmentation (+3 score)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.network?.vpnRequired || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        network: { ...elementSecurity.network, vpnRequired: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">VPN Required for Access (+2 score)</span>
                </label>
              </div>

              {/* Backups */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Backups</h4>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementSecurity.backups?.enabled || false}
                    onChange={(e) =>
                      setElementSecurity({
                        ...elementSecurity,
                        backups: { ...elementSecurity.backups, enabled: e.target.checked },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Backups Enabled (+3 score)</span>
                </label>

                {elementSecurity.backups?.enabled && (
                  <div className="ml-6 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-foreground-secondary mb-1">Frequency</label>
                      <select
                        value={elementSecurity.backups?.frequency || ''}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            backups: { ...elementSecurity.backups, frequency: e.target.value },
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      >
                        <option value="">Select frequency...</option>
                        <option value="Continuous">Continuous (+2 score)</option>
                        <option value="Hourly">Hourly (+1 score)</option>
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                      </select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.backups?.encryption || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            backups: { ...elementSecurity.backups, encryption: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">Backups Encrypted (+2 score)</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.backups?.offsite || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            backups: { ...elementSecurity.backups, offsite: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">Offsite Backups (+2 score)</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={elementSecurity.backups?.tested || false}
                        onChange={(e) =>
                          setElementSecurity({
                            ...elementSecurity,
                            backups: { ...elementSecurity.backups, tested: e.target.checked },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-foreground">Recovery Tested (+3 score)</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Threat Level */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Threat Level</label>
                <select
                  value={elementSecurity.threatLevel}
                  onChange={(e) =>
                    setElementSecurity({ ...elementSecurity, threatLevel: e.target.value as ThreatLevel })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Compliance Frameworks */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Compliance Frameworks</label>
                <div className="space-y-2">
                  {(['SOC2', 'ISO27001', 'GDPR', 'HIPAA', 'PCI-DSS', 'NIST'] as ComplianceFramework[]).map(
                    (framework) => (
                      <label key={framework} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={elementSecurity.complianceFrameworks.includes(framework)}
                          onChange={(e) => {
                            const frameworks = e.target.checked
                              ? [...elementSecurity.complianceFrameworks, framework]
                              : elementSecurity.complianceFrameworks.filter((f) => f !== framework);
                            setElementSecurity({ ...elementSecurity, complianceFrameworks: frameworks });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-foreground">{framework}</span>
                      </label>
                    )
                  )}
                </div>
              </div>

              {/* Security Score Preview */}
              <div className="p-4 bg-accent-muted border border-border rounded">
                <div className="text-sm text-foreground-secondary mb-1">Security Score Preview</div>
                <div className={`text-3xl font-bold ${getScoreColor(calculateSecurityScore(elementSecurity))}`}>
                  {calculateSecurityScore(elementSecurity)}/100
                </div>
              </div>

              <button
                onClick={handleSave}
                className="w-full px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover font-medium"
              >
                Save Security Settings
              </button>
            </div>
          </div>
        )}

        {/* Compliance View */}
        {viewMode === 'compliance' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Select Framework</label>
              <select
                value={selectedFramework}
                onChange={(e) => setSelectedFramework(e.target.value as ComplianceFramework)}
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
              >
                <option value="SOC2">SOC 2</option>
                <option value="ISO27001">ISO 27001</option>
                <option value="GDPR">GDPR</option>
                <option value="HIPAA">HIPAA</option>
                <option value="PCI-DSS">PCI-DSS</option>
                <option value="NIST">NIST</option>
                <option value="FedRAMP">FedRAMP</option>
                <option value="CIS">CIS Controls</option>
              </select>
            </div>

            {/* Framework Requirements */}
            {selectedElement && elementSecurity && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-foreground">
                  {getComplianceFrameworkRequirements(selectedFramework).name} Requirements
                </h3>

                <div className="p-3 bg-cream/5 border border-border rounded">
                  <p className="text-sm text-foreground-secondary">
                    {getComplianceFrameworkRequirements(selectedFramework).description}
                  </p>
                </div>

                <div className="space-y-3">
                  {getComplianceFrameworkRequirements(selectedFramework).keyRequirements.map((req) => {
                    const passes = req.check(elementSecurity);
                    return (
                      <div
                        key={req.id}
                        className={`p-4 rounded border ${
                          passes
                            ? 'bg-green-500/10 border-green-500/30'
                            : 'bg-red-500/10 border-red-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-mono text-foreground-tertiary">{req.id}</span>
                              <span className="text-xs px-2 py-0.5 rounded bg-cream/10 text-foreground-secondary">
                                {req.category}
                              </span>
                            </div>
                            <p className="text-sm text-foreground font-medium">{req.requirement}</p>
                          </div>
                          <div
                            className={`px-3 py-1 rounded text-xs font-semibold ${
                              passes ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                            }`}
                          >
                            {passes ? 'PASS' : 'FAIL'}
                          </div>
                        </div>

                        {!passes && (
                          <div className="mt-2 pt-2 border-t border-red-500/20">
                            <p className="text-xs text-red-400">
                              This requirement is not met. Review your security settings to ensure compliance.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Summary */}
                <div className="p-4 bg-accent-muted border border-border rounded">
                  <div className="text-sm text-foreground-secondary mb-1">Compliance Score</div>
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-foreground">
                      {
                        getComplianceFrameworkRequirements(selectedFramework).keyRequirements.filter(
                          (req) => req.check(elementSecurity)
                        ).length
                      }
                      /{getComplianceFrameworkRequirements(selectedFramework).keyRequirements.length}
                    </div>
                    <div className="text-sm text-foreground-tertiary">requirements met</div>
                  </div>
                </div>
              </div>
            )}

            {/* Diagram-wide Compliance Gaps */}
            <div className="pt-4 border-t border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Diagram-wide Compliance Gaps ({complianceGaps.length})
              </h3>

              {complianceGaps.length === 0 ? (
                <div className="p-8 text-center text-green-500">
                  <Shield className="w-12 h-12 mx-auto mb-3" />
                  <p>No compliance gaps found for {selectedFramework} across all elements!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {complianceGaps.map(({ element, gaps }, index) => (
                    <div key={index} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="font-medium text-foreground mb-2">{element.name}</div>
                      <ul className="space-y-1">
                        {gaps.map((gap, gapIndex) => (
                          <li key={gapIndex} className="text-sm text-yellow-400 flex items-start gap-2">
                            <span className="mt-1">•</span>
                            <span>{gap}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
